import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useI18N } from "@src/language/language-context";
import { updateShowOtaConfirmFirmwareInstallationModal } from "@src/reducers/your-reducer";
import { getSingleUserUseCase } from "@src/cloud-connect/user-manager/use-cases/get-single-user-use-case";
import { cancelInstallationProcessUseCase } from "@src/cloud-connect/ota-update/use-cases/cancel-installation-process-use-case";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import type { InstallAndActionBlock } from "@src/cloud-connect/ota-update/constants";
import { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { OTA_OBJECT_TYPE } from "@src/cloud-connect/ota-update/constants";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";

interface UseOtaRequestApprovalForAdminProps {
  activeInstallationData?: ActiveInstallationProcessPayload | null;
  setInstallAndActionBlock: React.Dispatch<
    React.SetStateAction<InstallAndActionBlock | null>
  >;
}

export const useOtaRequestApprovalForAdmin = ({
  activeInstallationData,
  setInstallAndActionBlock,
}: UseOtaRequestApprovalForAdminProps) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const [initiatorName, setInitiatorName] = useState<string | null>(null);
  const [isCancelRequestLoading, setIsCancelRequestLoading] = useState(false);
  const isDebugLoggingEnabled = useFeatureFlag("allow-debug", false);

  const isBackupInstallation =
    activeInstallationData?.firmware?.type === OTA_OBJECT_TYPE.FIRMWARE_BACKUP;
  const installationName = activeInstallationData?.firmware?.filename || "";

  const titleTemplate = isBackupInstallation
    ? i18n("modules.otaRequestApprovalForAdmin.titleTemplateBackup")
    : i18n("modules.otaRequestApprovalForAdmin.titleTemplateFirmware");

  const title = titleTemplate.replace("{itemName}", installationName);

  const itemNameMarker = "__ITEM_NAME__";
  const titleTemplateWithMarker = titleTemplate.replace(
    "{itemName}",
    itemNameMarker,
  );
  const [titlePrefix, titleSuffix = ""] =
    titleTemplateWithMarker.split(itemNameMarker);

  const texts = {
    title,
    titleHasItemName: titleTemplate.includes("{itemName}"),
    titlePrefix,
    titleSuffix,
    itemName: installationName,
    descriptionTemplate: isBackupInstallation
      ? i18n("modules.otaRequestApprovalForAdmin.descriptionTemplateBackup")
      : i18n("modules.otaRequestApprovalForAdmin.descriptionTemplateFirmware"),
    reviewAndApproveButtonText: i18n(
      "modules.otaRequestApprovalForAdmin.reviewAndApproveButtonText",
    ),
    cancelRequestButtonText: i18n(
      "modules.otaRequestApprovalForAdmin.cancelRequestButtonText",
    ),
    cancelSuccessMessage: i18n(
      "modules.otaRequestApprovalForAdmin.cancelSuccessMessage",
    ),
    cancelFailureMessage: i18n(
      "modules.otaRequestApprovalForAdmin.cancelFailureMessage",
    ),
  };

  const handleReviewRequestAndApprove = () => {
    dispatch(updateShowOtaConfirmFirmwareInstallationModal(true));
  };

  const handleCancelRequest = async () => {
    if (isCancelRequestLoading) {
      return;
    }

    const updateId = activeInstallationData?.update_id;

    if (!updateId) {
      return;
    }

    setIsCancelRequestLoading(true);

    try {
      const response = await cancelInstallationProcessUseCase(updateId);

      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Response from cancelInstallationProcessUseCase:",
          response,
        );

      if (response.success) {
        setInstallAndActionBlock(null);
        successSnackbar(texts.cancelSuccessMessage, 5000);
        return;
      }

      errorSnackbar(texts.cancelFailureMessage, 5000);
      setIsCancelRequestLoading(false);
    } catch {
      errorSnackbar(texts.cancelFailureMessage, 5000);
      setIsCancelRequestLoading(false);
    }
  };

  useEffect(() => {
    async function fetchInitiatorName() {
      if (!activeInstallationData?.initiator_id) {
        setInitiatorName("");
        return;
      }

      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Fetching initiator details for ID:",
          activeInstallationData?.initiator_id,
        );

      const response = await getSingleUserUseCase(
        activeInstallationData?.initiator_id,
      );

      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Response from getSingleUserUseCase (initiator):",
          response,
        );

      if (response.success && response.data?.display_name) {
        setInitiatorName(response.data?.display_name);
      } else {
        setInitiatorName("");
      }
    }

    fetchInitiatorName();
  }, [activeInstallationData?.initiator_id]);

  const descriptionText = texts.descriptionTemplate.replace(
    "{initiator}",
    initiatorName || "",
  );

  return {
    texts,
    descriptionText,
    showContent: initiatorName !== null,
    handleReviewRequestAndApprove,
    handleCancelRequest,
    isCancelRequestLoading,
  };
};
